import { memo, useCallback, useMemo } from 'react';
import Advanced from 'components/Advanced';
import Comments from 'components/Comments';
import DiscardConfirmation, {
  useDiscardConfirmation,
} from 'components/DiscardConfirmation';
import DrawerExtraButtons from 'components/DrawerExtraButtons';
import useSelectResource from 'hooks/useSelectResource';
import { getText } from 'i18n/intl';
import {
  getDrawerData,
  onDiscardForm,
  requestDeleteObjectTypes,
  requestUpdateObjectType,
  selectFirstDrawer,
  selectObjectTypesMap,
  selectSecondDrawer,
  updateDrawerObjectType,
} from 'slices/objectTypes.slice';
import { useAppDispatch, useAppSelector } from 'slices/store';
import type { TObjectTypesDetail } from 'types/state.interface';
import { convertDataTypes2Form } from 'utils/objectTypes';
import { TEDrawer, TEDrawerSection } from '@timeedit/ui-components';
import Attributes from './sections/Attributes';
import { GeneralInfo } from './sections/GeneralInfo';
import { Language } from './sections/Language';
import { Properties } from './sections/Properties';
import ReservationTemplate from './sections/ReservationTemplate';

const ObjectTypesDrawer = () => {
  const dispatch = useAppDispatch();

  const firstDrawer = useAppSelector(selectFirstDrawer);
  const secondDrawer = useAppSelector(selectSecondDrawer);
  const objectTypesMap = useAppSelector(selectObjectTypesMap);

  const setFirstDrawer = useCallback(
    (data: TObjectTypesDetail) => {
      dispatch(updateDrawerObjectType({ isFirstDrawer: true, data }));
    },
    [dispatch]
  );

  const setSecondDrawer = useCallback(
    (data: TObjectTypesDetail) => {
      dispatch(updateDrawerObjectType({ isFirstDrawer: false, data }));
    },
    [dispatch]
  );

  useSelectResource(
    'integer',
    objectTypesMap,
    firstDrawer,
    secondDrawer,
    setFirstDrawer,
    setSecondDrawer
  );

  const setDataFirstDrawer = useCallback(
    ({ key, value }: { key: string; value: any }) => {
      const firstDrawer = dispatch(getDrawerData(true));
      if (!firstDrawer) return;
      setFirstDrawer({
        ...firstDrawer,
        [key]: value,
        isEdited: true,
      });
    },
    [dispatch, setFirstDrawer]
  );
  const setDataSecondDrawer = useCallback(
    ({ key, value }: { key: string; value: any }) => {
      const secondDrawer = dispatch(getDrawerData(false));
      if (!secondDrawer) return;
      setSecondDrawer({
        ...secondDrawer,
        [key]: value,
        isEdited: true,
      });
    },
    [dispatch, setSecondDrawer]
  );
  const handleSaveFirstDrawer = async () => {
    dispatch(
      requestUpdateObjectType({
        isFirstDrawer: true,
        data: firstDrawer,
      })
    );
  };
  const handleSaveSecondDrawer = async () => {
    dispatch(
      requestUpdateObjectType({
        isFirstDrawer: false,
        data: secondDrawer,
      })
    );
  };
  const delFirstDrawer = async () => {
    const delResult = await dispatch(
      requestDeleteObjectTypes([firstDrawer?.id])
    );
    if (delResult) {
      setFirstDrawer(null);
    }
  };
  const delSecondDrawer = async () => {
    const delResult = await dispatch(
      requestDeleteObjectTypes([secondDrawer?.id])
    );
    if (delResult) {
      setSecondDrawer(null);
    }
  };
  const [
    openDiscardDialog,
    onCloseDiscardDialog,
    confirmFn,
    confirmDiscardChanged,
  ] = useDiscardConfirmation();

  return (
    <>
      <TEDrawer
        expandWidth={'70vw'}
        firstDrawer={{
          title: firstDrawer?.name,
          content: <Content onChange={setDataFirstDrawer} data={firstDrawer} />,
          extra: (
            <DrawerExtraButtons
              confirmDeleteMsg={getText('object_type_confirmation_delete')}
              handleDelete={delFirstDrawer}
              handleDiscard={() =>
                dispatch(
                  onDiscardForm({ isFirstDrawer: true, id: firstDrawer?.id })
                )
              }
              handleSave={handleSaveFirstDrawer}
              isSaveDisable={!firstDrawer?.isEdited as any}
            />
          ),
          drawerOpen: !!firstDrawer,
          setDrawerOpen: (val) => {
            if (!val && firstDrawer?.isEdited) {
              confirmDiscardChanged(() => {
                if (firstDrawer?.id === '0') {
                  dispatch(requestDeleteObjectTypes(['0']));
                }
                setFirstDrawer(null);
              });
              return false;
            }
            if (firstDrawer?.id === '0') {
              dispatch(requestDeleteObjectTypes(['0']));
            }
            setFirstDrawer(null);
          },
        }}
        secondDrawer={{
          title: secondDrawer?.name,
          content: (
            <Content onChange={setDataSecondDrawer} data={secondDrawer} />
          ),
          drawerOpen: !!secondDrawer,
          setDrawerOpen: (val) => {
            if (!val && secondDrawer?.isEdited) {
              confirmDiscardChanged(() => {
                setSecondDrawer(null);
              });
              return false;
            }
            setSecondDrawer(null);
          },
          extra: (
            <DrawerExtraButtons
              confirmDeleteMsg={getText('object_type_confirmation_delete')}
              handleDelete={delSecondDrawer}
              handleDiscard={() =>
                dispatch(
                  onDiscardForm({ isFirstDrawer: false, id: secondDrawer?.id })
                )
              }
              handleSave={handleSaveSecondDrawer}
              isSaveDisable={!secondDrawer?.isEdited as any}
            />
          ),
        }}
        onExpand={() => null}
      />
      <DiscardConfirmation
        open={openDiscardDialog}
        onClose={onCloseDiscardDialog}
        confirmFn={confirmFn}
      />
    </>
  );
};
type TContent = {
  data: TObjectTypesDetail;
  onChange: (data: { key: string; value: any }) => void;
};
const Content = memo(({ data, onChange }: TContent) => {
  const value = useMemo(() => convertDataTypes2Form(data), [data]);
  const onChangeValue = (key: string, value: any) => {
    onChange({ key, value });
  };
  return (
    <>
      <GeneralInfo value={value} onChangeValue={onChangeValue} />
      <Properties value={value} onChangeValue={onChangeValue} />
      <Attributes value={value} onChangeValue={onChangeValue} />
      <ReservationTemplate value={value} onChangeValue={onChangeValue} />
      <TEDrawerSection label="Advanced">
        <Advanced
          options={value?.advancedSettings || []}
          selectedUrl={'types'}
          selectedId={value.id}
        />
      </TEDrawerSection>
      <Language value={value} onChangeValue={onChangeValue} />
      <Comments
        value={value.documentation}
        onChange={(e) => onChangeValue('documentation', e)}
      />
    </>
  );
});

export default ObjectTypesDrawer;
